import React from 'react'
import { Modal, Button, Checkbox, Card, Tag, Space, Typography, Row, Col, Divider } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHeartbeat, faThermometer, faLungs, faTint, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons'

const { Text, Title } = Typography

const DataExtractionModal = ({
  isOpen,
  onClose,
  extractedVitals,
  selectedVitals,
  onToggleVital,
  onSubmit,
  isSubmitting
}) => {
  // Get icon for vital type
  const getVitalIcon = (key) => {
    const iconMap = {
      bloodPressure: faTint,
      pulse: faHeartbeat,
      temperature: faThermometer,
      oxygenSaturation: faLungs,
      respiratoryRate: faLungs
    }
    return iconMap[key] || faHeartbeat
  }

  // Get color for confidence level
  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return '#52c41a' // Green
    if (confidence >= 0.8) return '#1890ff' // Blue
    if (confidence >= 0.7) return '#faad14' // Orange
    return '#ff4d4f' // Red
  }

  // Format vital value for display
  const formatVitalValue = (vital) => {
    if (vital.key === 'bloodPressure' && typeof vital.value === 'object') {
      return `${vital.value.systolic}/${vital.value.diastolic} ${vital.unit}`
    }
    return `${vital.value} ${vital.unit}`
  }

  // Get selected count
  const selectedCount = Object.values(selectedVitals).filter(Boolean).length

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FontAwesomeIcon icon={faHeartbeat} style={{ color: '#1890ff' }} />
          <span>Extracted Vital Signs</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key='cancel' onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key='submit'
          type='primary'
          loading={isSubmitting}
          disabled={selectedCount === 0}
          onClick={onSubmit}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        >
          Submit {selectedCount > 0 ? `(${selectedCount})` : ''} Vital Signs
        </Button>
      ]}
    >
      <div style={{ marginBottom: '16px' }}>
        <Text type='secondary'>
          Select the vital signs you want to submit to the patient's record. High-confidence readings are pre-selected.
        </Text>
      </div>

      <Divider />

      {extractedVitals.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Text type='secondary'>No vital signs extracted</Text>
        </div>
      ) : (
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <Row gutter={[16, 16]}>
            {extractedVitals.map((vital, index) => (
              <Col span={24} key={index}>
                <Card
                  size='small'
                  style={{
                    borderLeft: `4px solid ${selectedVitals[index] ? '#52c41a' : '#d9d9d9'}`,
                    backgroundColor: selectedVitals[index] ? '#f6ffed' : '#fafafa'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                      <Checkbox
                        checked={selectedVitals[index] || false}
                        onChange={() => onToggleVital(index)}
                        style={{ marginRight: '8px' }}
                      />

                      <FontAwesomeIcon
                        icon={getVitalIcon(vital.key)}
                        style={{
                          color: selectedVitals[index] ? '#52c41a' : '#8c8c8c',
                          fontSize: '18px'
                        }}
                      />

                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <Text strong style={{ fontSize: '15px' }}>
                            {vital.label}
                          </Text>
                          <Tag
                            color={getConfidenceColor(vital.confidence)}
                            style={{ fontSize: '11px', lineHeight: '16px' }}
                          >
                            {Math.round(vital.confidence * 100)}% confidence
                          </Tag>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Text style={{ fontSize: '16px', fontWeight: 600, color: '#1890ff' }}>
                            {formatVitalValue(vital)}
                          </Text>

                          {vital.sourceText && (
                            <Text type='secondary' style={{ fontSize: '12px', fontStyle: 'italic' }}>
                              "{vital.sourceText}"
                            </Text>
                          )}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {selectedVitals[index] ? (
                        <Tag color='success' icon={<FontAwesomeIcon icon={faCheck} />}>
                          Selected
                        </Tag>
                      ) : (
                        <Tag color='default' icon={<FontAwesomeIcon icon={faTimes} />}>
                          Not Selected
                        </Tag>
                      )}
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}

      {extractedVitals.length > 0 && (
        <>
          <Divider />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type='secondary'>
              {selectedCount} of {extractedVitals.length} vital signs selected
            </Text>

            <Space>
              <Button
                size='small'
                onClick={() => {
                  const allSelected = {}
                  extractedVitals.forEach((_, index) => {
                    allSelected[index] = true
                  })
                  extractedVitals.forEach((_, index) => onToggleVital(index))
                }}
              >
                Select All
              </Button>

              <Button
                size='small'
                onClick={() => {
                  extractedVitals.forEach((_, index) => {
                    if (selectedVitals[index]) {
                      onToggleVital(index)
                    }
                  })
                }}
              >
                Deselect All
              </Button>
            </Space>
          </div>
        </>
      )}
    </Modal>
  )
}

export default DataExtractionModal
